import { z } from "zod";
import type { SerializableValue } from "../types/serializable-zod-schema.types.js";

/**
 * Opaque-but-serializable value: the JSON primitives plus `Date` (which
 * `JSON.stringify` emits as ISO) and `undefined` (which `JSON.stringify`
 * drops), recursively through arrays and string-keyed records. Mirrors Zod's
 * built-in `z.json()` with `Date` and `undefined` added as allowed leaves — so
 * the runtime schema matches every leaf of the `SerializableValue` type.
 *
 * Serializable **by construction**: a field built from this schema can only
 * ever hold values that survive `JSON.stringify`, so it is guaranteed
 * controller-returnable. Prefer this over `z.custom<SerializableValue>()`,
 * which asserts the type without enforcing it — a developer could assert a
 * non-serializable type and the schema would not object.
 */
export const serializableValueSchema: z.ZodType<SerializableValue> = z.lazy(
  () =>
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.date(),
      z.null(),
      z.undefined(),
      z.array(serializableValueSchema),
      z.record(z.string(), serializableValueSchema),
    ]),
);
